import React from 'react';
import { alertTagStyles } from '../../../Helper/DigitalTwin/DataFile';
import { formatDateTime } from '../../../Helper/DatePicker/DateFormatters';
import { ANNOTATION_SOURCE, getAnnotationSourceDisplay } from '../Common/CommonFunctions';

// Inline SVGs so the tag icon survives static-markup rendering (this tooltip is
// serialized to an HTML string, not mounted as a live tree). Paths mirror the
// nav icons `bms-fault-icon` and `smart-alert-icon`; all fills use the source
// color so the tag reads as a single-color glyph.
const BmsFaultTagIcon = ({ color, bgColor }) => (
    <svg width="14" height="14" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M14.8438 4.15625H2.375C1.90258 4.15625 1.44952 4.34392 1.11547 4.67797C0.781417 5.01202 0.59375 5.46508 0.59375 5.9375V13.0625C0.59375 13.5349 0.781417 13.988 1.11547 14.322C1.44952 14.6561 1.90258 14.8438 2.375 14.8438H14.8438C15.3162 14.8438 15.7692 14.6561 16.1033 14.322C16.4373 13.988 16.625 13.5349 16.625 13.0625V5.9375C16.625 5.46508 16.4373 5.01202 16.1033 4.67797C15.7692 4.34392 15.3162 4.15625 14.8438 4.15625ZM8.01562 6.53125C8.01562 6.37378 8.07818 6.22276 8.18953 6.11141C8.30088 6.00006 8.4519 5.9375 8.60938 5.9375C8.76685 5.9375 8.91787 6.00006 9.02922 6.11141C9.14057 6.22276 9.20312 6.37378 9.20312 6.53125V9.5C9.20312 9.65747 9.14057 9.8085 9.02922 9.91985C8.91787 10.0312 8.76685 10.0938 8.60938 10.0938C8.4519 10.0938 8.30088 10.0312 8.18953 9.91985C8.07818 9.8085 8.01562 9.65747 8.01562 9.5V6.53125ZM8.60938 13.0625C8.43323 13.0625 8.26103 13.0103 8.11457 12.9124C7.96811 12.8145 7.85395 12.6754 7.78654 12.5127C7.71914 12.35 7.7015 12.1709 7.73586 11.9981C7.77023 11.8254 7.85505 11.6667 7.97961 11.5421C8.10416 11.4176 8.26286 11.3327 8.43562 11.2984C8.60839 11.264 8.78746 11.2816 8.9502 11.349C9.11294 11.4165 9.25204 11.5306 9.3499 11.6771C9.44777 11.8235 9.5 11.9957 9.5 12.1719C9.5 12.4081 9.40617 12.6346 9.23914 12.8016C9.07212 12.9687 8.84558 13.0625 8.60938 13.0625ZM19 7.125V11.875C19 12.0325 18.9374 12.1835 18.8261 12.2948C18.7147 12.4062 18.5637 12.4688 18.4062 12.4688C18.2488 12.4688 18.0978 12.4062 17.9864 12.2948C17.8751 12.1835 17.8125 12.0325 17.8125 11.875V7.125C17.8125 6.96753 17.8751 6.81651 17.9864 6.70516C18.0978 6.59381 18.2488 6.53125 18.4062 6.53125C18.5637 6.53125 18.7147 6.59381 18.8261 6.70516C18.9374 6.81651 19 6.96753 19 7.125Z" fill={color} />
        {/* Exclamation drawn in the tag background so it reads as a cutout on the battery body, matching the nav icon. */}
        <circle cx="8.44995" cy="12.25" r="1.25" fill={bgColor} />
        <rect x="7.40002" y="5.5" width="2" height="5" rx="1" fill={bgColor} />
    </svg>
);

const SmartAlertTagIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M16.467 6.17196C17.2909 7.89517 17.4473 9.8625 16.906 11.6943C16.3647 13.526 15.1641 15.0923 13.5359 16.0909C11.9076 17.0895 9.96723 17.4496 8.08913 17.1016C6.21103 16.7537 4.52839 15.7224 3.36596 14.2068C2.20352 12.6912 1.64369 10.7987 1.79453 8.89462C1.94536 6.99053 2.79615 5.2098 4.18271 3.89612C5.56927 2.58243 7.39329 1.82891 9.30275 1.781C11.2122 1.73308 13.0717 2.39416 14.5224 3.63665L16.205 1.95337C16.3164 1.84196 16.4675 1.77936 16.625 1.77936C16.7826 1.77936 16.9337 1.84196 17.0451 1.95337C17.1565 2.06478 17.2191 2.21588 17.2191 2.37344C17.2191 2.531 17.1565 2.68211 17.0451 2.79352L12.4391 7.40028L9.64032 10.1991L7.86278 11.9766C8.32233 12.2806 8.85739 12.4506 9.4081 12.4678C9.95881 12.4849 10.5034 12.3485 10.981 12.0737C11.4586 11.799 11.8503 11.3967 12.1122 10.912C12.3742 10.4273 12.4961 9.87928 12.4643 9.32923C12.4555 9.17175 12.5095 9.01721 12.6146 8.8996C12.7197 8.78199 12.8672 8.71094 13.0247 8.70208C13.1822 8.69322 13.3367 8.74728 13.4543 8.85237C13.5719 8.95745 13.643 9.10496 13.6518 9.26243C13.6997 10.1032 13.4911 10.9386 13.0536 11.6582C12.6161 12.3777 11.9703 12.9474 11.2018 13.2917C10.4333 13.636 9.57832 13.7388 8.75011 13.5864C7.9219 13.434 7.15953 13.0336 6.56395 12.4382C5.82323 11.6948 5.39121 10.6986 5.35463 9.64971C5.31804 8.60085 5.67961 7.57698 6.36673 6.78368C7.05385 5.99039 8.01564 5.48641 9.05899 5.37295C10.1023 5.25948 11.15 5.54492 11.9916 6.17196L13.6786 4.482C12.4412 3.45466 10.8679 2.92071 9.2608 2.98269C7.65373 3.04467 6.12622 3.69822 4.97165 4.81781C3.81708 5.93741 3.11687 7.4441 3.00549 9.0485C2.89412 10.6529 3.37943 12.2419 4.36823 13.5103C5.35703 14.7787 6.77959 15.637 8.36268 15.9204C9.94578 16.2039 11.5778 15.8924 12.9452 15.0459C14.3127 14.1994 15.3192 12.8775 15.7714 11.3341C16.2236 9.79074 16.0896 8.1347 15.3952 6.68407C15.3273 6.54195 15.3187 6.37868 15.3711 6.23016C15.4236 6.08165 15.5329 5.96007 15.675 5.89216C15.8172 5.82425 15.9804 5.81557 16.129 5.86805C16.2775 5.92052 16.399 6.02984 16.467 6.17196Z" fill={color} />
    </svg>
);

// Icon per source; label + colors come from the shared source-display config
// so the tag matches the chart hover tooltip. Unknown/legacy -> BMS faults.
const ICON_BY_SOURCE = {
    [ANNOTATION_SOURCE.SMART_ALERT]: SmartAlertTagIcon,
    [ANNOTATION_SOURCE.BMS_FAULT]: BmsFaultTagIcon,
};

const getSourceIcon = (source) =>
    ICON_BY_SOURCE[source] || ICON_BY_SOURCE[ANNOTATION_SOURCE.BMS_FAULT];

const AnnotationTooltip = ({ timePoint, title = 'Events' }) => {
    // Handle both old format (just annotations array) and new format (timePoint object)
    const startAnnotations = timePoint?.startAnnotations || [];
    const endAnnotations = timePoint?.endAnnotations || [];
    const allAnnotations = timePoint?.annotations || timePoint || [];

    // Helper function to format annotation data
    const formatAnnotation = (annotation) => {
        // Format dates - timestamps are already converted to user timezone
        const activatedTime = formatDateTime(annotation.customerDBTimeInsert);
        const resolvedTime = annotation.resolveCustomerDBTime ? formatDateTime(annotation.resolveCustomerDBTime) : null;

        // Create date range string for resolved alerts
        const formattedDate = resolvedTime
            ? `${activatedTime} - ${resolvedTime}`
            : activatedTime;

        // Get severity display text with proper capitalization
        const severityText = annotation.severity
            ? annotation.severity.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            : 'Warning';

        // Get status chip color from DataFile
        const statusKey = annotation.bmsAlertStatus?.toLowerCase().trim() || 'open';
        const statusStyle = alertTagStyles.bmsAlertStatus[statusKey] || alertTagStyles.bmsAlertStatus.default;
        const statusColor = {
            background: statusStyle.backgroundColor,
            color: statusStyle.fontColor
        };

        // Get severity chip color from DataFile
        const severityKey = annotation.severity?.toLowerCase().trim() || 'warning';
        const severityStyle = alertTagStyles.severity[severityKey] || alertTagStyles.severity.default;
        const severityColor = {
            background: severityStyle.backgroundColor,
            color: severityStyle.fontColor
        };

        return {
            formattedDate,
            severityText,
            statusColor,
            severityColor
        };
    };

    // Helper to render annotation boxes
    const renderAnnotationBox = (annotation, index, totalCount) => {
        const { formattedDate, severityText, statusColor, severityColor } = formatAnnotation(annotation);
        const sourceDisplay = getAnnotationSourceDisplay(annotation.source);
        const SourceIcon = getSourceIcon(annotation.source);
        const isBmsFault = annotation.source !== ANNOTATION_SOURCE.SMART_ALERT;

        return (
            <div
                key={index}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    minWidth: '415px',
                    backgroundColor: '#FAFAFA',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: index < totalCount - 1 ? '12px' : '0'
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Incident Name + source tag */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span
                            style={{
                                fontSize: '14px',
                                lineHeight: '18px',
                                fontWeight: 500,
                                color: '#000000',
                            }}
                        >
                            {annotation.incidentErrorName || 'Fault'}
                        </span>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: sourceDisplay.bg,
                                color: sourceDisplay.color,
                                padding: '2px 8px',
                                borderRadius: '500px',
                                fontWeight: 600,
                                fontSize: '11px',
                                lineHeight: '16px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <SourceIcon color={sourceDisplay.color} bgColor={sourceDisplay.bg} />
                            {sourceDisplay.label}
                        </span>
                    </div>
                    <div
                        style={{
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '20px',
                            color: '#494949',
                        }}
                    >
                        {formattedDate}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: statusColor.background,
                            color: statusColor.color,
                            padding: '2px 12px',
                            borderRadius: '500px',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '20px',
                        }}
                    >
                        {annotation.bmsAlertStatus || 'Open'}
                    </div>
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: severityColor.background,
                            color: severityColor.color,
                            padding: '0 12px',
                            borderRadius: '4px',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '24px',
                        }}
                    >
                        {severityText}
                    </div>
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: '#F0F0F0',
                            color: '#000000',
                            padding: '0 12px',
                            borderRadius: '4px',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '24px',
                        }}
                    >
                        {annotation.entityAffectedDisplayID || 'Battery System'}
                    </div>
                    {/* Level applies to BMS faults only; hidden for smart alerts. */}
                    {isBmsFault && annotation.severityLevel && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                background: '#F0F0F0',
                                color: '#000000',
                                padding: '0 12px',
                                borderRadius: '4px',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '24px',
                            }}
                        >
                            LV{annotation.severityLevel}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const hasTriggered = startAnnotations.length > 0;
    const hasResolved = endAnnotations.length > 0;

    return (
        <div
            style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '200px',
                fontFamily: 'Roboto, sans-serif',
            }}
        >
            {/* Title */}
            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '20px',
                    color: '#000000',
                    marginBottom: '12px',
                }}
            >
                {title}
            </div>

            {/* Triggered Section */}
            {hasTriggered && (
                <div style={{ marginBottom: hasResolved ? '16px' : '0' }}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: '20px',
                            color: '#666666',
                            marginBottom: '8px',
                        }}
                    >
                        Triggered
                    </div>
                    {startAnnotations.map((annotation, index) =>
                        renderAnnotationBox(annotation, index, startAnnotations.length)
                    )}
                </div>
            )}

            {/* Resolved Section */}
            {hasResolved && (
                <div>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: '20px',
                            color: '#666666',
                            marginBottom: '8px',
                        }}
                    >
                        Resolved
                    </div>
                    {endAnnotations.map((annotation, index) =>
                        renderAnnotationBox(annotation, index, endAnnotations.length)
                    )}
                </div>
            )}

            {/* Fallback for old format (if neither startAnnotations nor endAnnotations exist) */}
            {!hasTriggered && !hasResolved && allAnnotations.length > 0 && (
                <div>
                    {allAnnotations.map((annotation, index) =>
                        renderAnnotationBox(annotation, index, allAnnotations.length)
                    )}
                </div>
            )}
        </div>
    );
};

export default AnnotationTooltip;
